import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../service/Api';

const CollateralList = ({ companyId, onSelectCollateral }) => {
  const [collaterals, setCollaterals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadCollaterals();
  }, [companyId]);

  const loadCollaterals = async () => {
    setLoading(true);
    setError(null);
    try {
      // Use getAllCollaterals with optional companyId parameter
      const response = await apiService.getAllCollaterals(companyId || null);
      
      if (response.data.success) {
        setCollaterals(response.data.data || []);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load collaterals');
      console.error('Error loading collaterals:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewCollateral = (collateral) => {
    if (onSelectCollateral) {
      onSelectCollateral(collateral);
    } else if (collateral.collateral_slug) {
      navigate(`/collateral/${collateral.collateral_slug}`);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
        <div className="text-center py-8 text-gray-500">Loading collaterals...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
        <div className="p-4 bg-red-50 border border-red-200 rounded-md text-red-700">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Your Collaterals</h3>
        <button
          onClick={loadCollaterals}
          className="text-sm px-3 py-1 text-blue-600 hover:text-blue-700 transition-colors"
        >
          Refresh
        </button>
      </div>

      {collaterals.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p>No collaterals found.</p>
          <p className="text-sm mt-2">Create your first collateral to get started.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {collaterals.map((collateral) => (
            <div
              key={collateral.id}
              className="p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-md transition-all cursor-pointer"
              onClick={() => handleViewCollateral(collateral)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900 mb-1">
                    {collateral.title || 'Untitled Collateral'}
                  </h4>
                  {collateral.collateral_slug && (
                    <p className="text-sm text-gray-500 mb-2">
                      Slug: {collateral.collateral_slug}
                    </p>
                  )}
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span>Created: {formatDate(collateral.created_at)}</span>
                    {collateral.updated_at && (
                      <span>Updated: {formatDate(collateral.updated_at)}</span>
                    )}
                  </div>
                </div>
                <svg
                  className="w-5 h-5 text-gray-400 ml-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CollateralList;
